import { tokens } from './tokens';

/**
 * Generates the global stylesheet that materializes our design tokens
 * as CSS variables. Tailwind reads these via `rgb(var(--color-x) / <alpha>)`.
 *
 * Two scopes:
 *   :root  → light tokens (default)
 *   .dark  → dark tokens (toggled by adding `dark` class to <html>)
 */
// Widened parameter types so both light and dark palettes (each `as const` with
// distinct literal types) satisfy the same signature.
type ColorPalette = Record<keyof typeof tokens.light.palette, string>;
type ShadowSet = Record<keyof typeof tokens.light.shadows, string>;

function buildStylesheet(): string {
  const colorVars = (palette: ColorPalette) =>
    Object.entries(palette)
      .map(([k, v]) => `  --color-${kebab(k)}: ${v};`)
      .join('\n');

  const shadowVars = (shadows: ShadowSet) =>
    Object.entries(shadows)
      .map(([k, v]) => `  --shadow-${kebab(k)}: ${v};`)
      .join('\n');

  const motionVars = Object.entries(tokens.motion)
    .map(([k, v]) => `  --motion-${kebab(k)}: ${v};`)
    .join('\n');

  const radiusVars = Object.entries(tokens.radii)
    .map(([k, v]) => `  --radius-${k}: ${v};`)
    .join('\n');

  return `
:root {
${colorVars(tokens.light.palette)}
${shadowVars(tokens.light.shadows)}
${motionVars}
${radiusVars}
}
.dark {
${colorVars(tokens.dark.palette)}
${shadowVars(tokens.dark.shadows)}
}
`;
}

function kebab(s: string): string {
  return s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}

/** Inject token CSS variables BEFORE React mounts. Call once from main.tsx. */
export function mountThemeVariables(): void {
  const id = 'app-theme-tokens';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = buildStylesheet();
  document.head.appendChild(style);
}

// ────────────────────────────────────────────────────────────────────────────
// Color mode
// ────────────────────────────────────────────────────────────────────────────

export type ColorMode = 'light' | 'dark';

const STORAGE_KEY = 'trendz.colorMode';
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

/**
 * Fired on `window` whenever the applied color mode changes *within this tab*.
 * The browser's `storage` event only fires in OTHER tabs, so without this an
 * in-tab toggle would leave same-tab `useColorMode()` consumers (notably the
 * MUI theme builder in main.tsx) stale until a reload. `detail` carries the
 * new mode.
 */
export const COLOR_MODE_CHANGE_EVENT = 'trendz:colormodechange';

/** Read a previously-stored explicit choice, if any. */
export function getStoredColorMode(): ColorMode | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === 'light' || raw === 'dark' ? raw : null;
  } catch {
    // localStorage can throw in private-browsing / disabled-cookies contexts.
    return null;
  }
}

/** What the OS / browser currently prefers (live; reads on each call). */
export function getSystemColorMode(): ColorMode {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia(DARK_MEDIA_QUERY).matches ? 'dark' : 'light';
}

/** Resolve which mode should be active on first paint: stored > system > light. */
export function resolveInitialColorMode(): ColorMode {
  return getStoredColorMode() ?? getSystemColorMode();
}

/** Apply the resolved mode to <html> *before* React mounts. Call once from main.tsx. */
export function applyInitialColorMode(): void {
  setColorModeClass(resolveInitialColorMode());
}

/** Read the currently-applied mode straight off <html>. */
export function getCurrentColorMode(): ColorMode {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

/**
 * Switch color mode and persist the choice. Persisting marks the user's
 * preference as "explicit" — once set, system-pref changes are ignored.
 * Pass `{ persist: false }` for transient toggles (we don't have any today).
 */
export function setColorMode(
  mode: ColorMode,
  options: { persist?: boolean } = {},
): void {
  const { persist = true } = options;
  setColorModeClass(mode);
  if (!persist) return;
  try {
    localStorage.setItem(STORAGE_KEY, mode);
    // Read-back guard: some browsers (Safari ITP, private mode, storage
    // disabled by policy) accept setItem silently then return null on read.
    // Surface that loudly so persistence-bug reports can be diagnosed.
    const readBack = localStorage.getItem(STORAGE_KEY);
    if (readBack !== mode) {
      console.warn(
        `[colorMode] localStorage persist failed silently: wrote "${mode}", read "${readBack ?? 'null'}"`,
      );
    }
  } catch (err) {
    console.warn('[colorMode] localStorage persist threw:', err);
  }
}

function setColorModeClass(mode: ColorMode): void {
  document.documentElement.classList.toggle('dark', mode === 'dark');
  // Broadcast within the tab so every useColorMode() instance re-syncs (the
  // cross-tab `storage` event never fires in the originating tab). Pre-React
  // calls (applyInitialColorMode) simply have no listeners yet — harmless.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(COLOR_MODE_CHANGE_EVENT, { detail: mode }),
    );
  }
}
